/**
 * Get Book Ideas — a Timeline door that proposes new books (#770 / #778).
 * Not Make cards. Not an add-card. Accept writes one Timeline idea pin
 * through persistAcceptedBookIdea — same book-board path as an in-chat accept.
 * Checked talks and notes can satisfy ends-or-keeps-going (#788)
 * and what holds the books together (#794 / #800). Existing
 * uploaded series note files on the project count, not only
 * Discovery chat notes (#796 / #800). A person, place, world,
 * or recurring problem already on the page is enough. Not a
 * required theme.
 */

import { GET_IDEAS_DOCS_LEDE } from './writerCopy';

export const GET_IDEAS_LABEL = 'Get Book Ideas';
export const GET_IDEAS_EDIT = 'Edit';
export const GET_IDEAS_DISCARD = 'Discard';
export const GET_IDEAS_ACCEPT = 'Accept';

export const GET_IDEAS_CHECKLIST_LEDE = GET_IDEAS_DOCS_LEDE;

export const GET_IDEAS_REVIEW_LEDE =
  'A few new book ideas. Accept one onto the Timeline as an idea, edit it first, or discard. It does not start a book.';

export const GET_IDEAS_ENDING_LEDE =
  'This series ends. We need a chosen ending before we can offer book ideas. Pick one of these, or write one.';

export const GET_IDEAS_ASK_ENDING =
  'This series ends. We need a chosen ending before we can offer book ideas. Name one — we will not invent one from a blank notebook.';

export const UNKNOWN_SHAPE_COPY =
  'We don’t know yet if this series ends, keeps going, or is a shared universe. Work that through in chat first.';

export const MISSING_THROUGH_LINE_COPY =
  'We don’t yet know what holds these books together. Work that through in chat first.';

export const DISCOVERY_POINTER = 'Open Series Discovery';

export const DISCOVERY_SHAPE_LEDE =
  'Optional — you can start a book first. This series can end, keep going, or share a world. Change it anytime. Choosing does not start a book.';

export const SIT_AFTER_PREFIX = 'This could sit';

export const GATE_UNKNOWN_SHAPE = 'unknown_shape';
export const GATE_MISSING_THROUGH_LINE = 'missing_through_line';
export const GATE_NEEDS_ENDING = 'needs_ending';
export const GATE_READY = 'ready';

export const SHAPE_ENDS = 'ends';
export const SHAPE_CONTINUES = 'continues';
export const SHAPE_SHARED_UNIVERSE = 'shared_universe';
export const SHAPE_UNKNOWN = 'unknown';

export const SHAPE_CHOICES = [
  {
    id: SHAPE_ENDS,
    persist: 'ends',
    label: 'This series ends',
    detail: 'A complete arc.',
  },
  {
    id: SHAPE_CONTINUES,
    persist: 'keeps going',
    label: 'This series keeps going',
    detail: 'Same lead, episodic.',
  },
  {
    id: SHAPE_SHARED_UNIVERSE,
    persist: 'shared universe',
    label: 'Shared universe',
    detail: 'Same world, different main people each book.',
  },
];

const CONTINUES = /\b(keeps?\s+going|keep\s+going|ongoing|continues?|episodic|open[- ]ended|never\s+ends?|no\s+end|more\s+books|does\s+not\s+end|doesn't\s+end|keeps\s+on|no\s+finale)\b/i;
const ENDS = /\b(ends?|ending|finite|closed|concludes?|last\s+book|finale|will\s+end|does\s+end|it\s+ends|has\s+an\s+end)\b/i;
const SHARED_UNIVERSE = (
  /\bshared\s+universe\b|\bshared\s+world\b|\bsame\s+world\b.{0,80}\bdifferent\b|\bdifferent\s+(?:main\s+)?(?:people|person|leads?|characters?|couples?|protagonists?)\b.{0,40}\beach\s+book\b|\beach\s+book\b.{0,40}\bdifferent\s+(?:main\s+)?(?:people|person|leads?|characters?|couples?|protagonists?)\b|\bside\s+(?:person|character|people)\b.{0,60}\blead\b/i
);

export function classifySeriesShape(endsOrContinues) {
  const blob = String(endsOrContinues || '').trim();
  if (!blob) return SHAPE_UNKNOWN;
  if (SHARED_UNIVERSE.test(blob)) return SHAPE_SHARED_UNIVERSE;
  const continues = CONTINUES.test(blob);
  const ends = ENDS.test(blob);
  if (continues && !ends) return SHAPE_CONTINUES;
  if (ends && !continues) return SHAPE_ENDS;
  if (continues) return SHAPE_CONTINUES;
  return SHAPE_UNKNOWN;
}

export function evaluateGetIdeasGate(project) {
  const shape = classifySeriesShape(project?.ends_or_continues);
  const through = String(project?.through_line_thing || '').trim();
  const ending = String(project?.series_ending || '').trim();
  if (shape === 'unknown') {
    return {
      gate: GATE_UNKNOWN_SHAPE,
      shape,
      copy: UNKNOWN_SHAPE_COPY,
      pointToDiscovery: true,
    };
  }
  if (!through) {
    return {
      gate: GATE_MISSING_THROUGH_LINE,
      shape,
      copy: MISSING_THROUGH_LINE_COPY,
      pointToDiscovery: true,
    };
  }
  if (shape === 'ends' && !ending) {
    return {
      gate: GATE_NEEDS_ENDING,
      shape,
      copy: GET_IDEAS_ENDING_LEDE,
      pointToDiscovery: false,
    };
  }
  return {
    gate: GATE_READY,
    shape,
    copy: '',
    pointToDiscovery: false,
  };
}

export function shouldShowSeriesShapeChooser({ project, story, session } = {}) {
  if (!project?.is_series || !project?.id) return false;
  if (story?.id) return false;
  const kind = session?.session_type;
  if (kind === 'explore' || kind === 'focus') return false;
  return true;
}

export function sitAfterNote(hint) {
  const text = String(hint || '').trim();
  if (!text) return '';
  if (/^this could sit\b/i.test(text)) return text;
  if (/^after\b/i.test(text)) return `${SIT_AFTER_PREFIX} ${text}`;
  return `${SIT_AFTER_PREFIX} after ${text}`;
}
